import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getAuth } from 'firebase/auth';
import { app } from '../firebase'; // Your Firebase initialization file
import Admin from '../pages/Admin';
import VerifyEmail from './VerifyEmail';
import { ByStream } from '../hooks/useStreams';
import { AUTH_STUCK_AFTER_MS, diagnoseBrowserState, resetBrowserState } from '../utils/sessionRecovery';

interface AuthenticatedRouteProps {
  children: ReactNode;
}

const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({ children }) => {
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [stuck, setStuck] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [, refresh] = useState(0); // the user object is updated in place when the email is confirmed, so a re-render is how we notice

  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setStuck(true);
      diagnoseBrowserState();
    }, AUTH_STUCK_AFTER_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/Signup'); // Redirect to signup if not loading and no user
      // navigate('/login'); // Redirect to login if not loading and no user
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <>
        <Admin />
        {stuck && (
          <div className="text-center mt-8 px-4" role="status">
            <p className="text-slate-700">Still checking your sign-in. If this page stays stuck, reset the saved browser data and sign in again.</p>
            <button
              className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold disabled:opacity-60"
              disabled={resetting}
              onClick={async () => {
                setResetting(true);
                await resetBrowserState();
                window.location.reload();
              }}
            >
              {resetting ? 'Resetting...' : 'Reset and reload'}
            </button>
          </div>
        )}
      </>
    );
  }

  if (user && !user.emailVerified) return <VerifyEmail user={user} onVerified={() => refresh((n) => n + 1)} />;

  return user ? <ByStream>{children}</ByStream> : null; // Render children if user exists, else render nothing
};

export default AuthenticatedRoute;
